(() => {
  const state = {
    globalsBound: false,
    activeDrawer: null,
    announcementInstances: new Map(),
    heroInstances: new Map(),
    productInstances: new Map()
  };

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (Number.isNaN(number) || number == null) return '0';
    const amount = (number / 100).toFixed(precision);
    const parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }

  function formatMoney(cents, format) {
    if (cents == null || cents === '') return '';
    let amount = cents;
    if (typeof amount === 'string') {
      amount = amount.replace('.', '');
      amount = Number(amount);
    }
    if (!Number.isFinite(amount)) amount = 0;

    const moneyFormat = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    if (!match) return moneyFormat;

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(amount, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amount, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(amount, 2, ',', '.');
        break;
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  function syncBodyOverflow() {
    const isDrawerOpen = Boolean(document.querySelector('.drawer.is-open'));
    document.body.classList.toggle('overflow-hidden', isDrawerOpen);
  }

  function openDrawer(drawerId) {
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;
    closeAllDrawers();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll(`[data-drawer-toggle="${drawerId}"]`).forEach((button) => button.setAttribute('aria-expanded', 'true'));
    state.activeDrawer = drawer;
    syncBodyOverflow();
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (drawer.id) {
      document.querySelectorAll(`[data-drawer-toggle="${drawer.id}"]`).forEach((button) => button.setAttribute('aria-expanded', 'false'));
    }
    if (state.activeDrawer === drawer) state.activeDrawer = null;
    syncBodyOverflow();
  }

  function closeAllDrawers() {
    document.querySelectorAll('.drawer.is-open').forEach((drawer) => closeDrawer(drawer));
  }

  function handleGlobalClick(event) {
    const drawerToggle = event.target.closest('[data-drawer-toggle]');
    if (drawerToggle) {
      const drawerId = drawerToggle.getAttribute('data-drawer-toggle');
      if (drawerId) openDrawer(drawerId);
      return;
    }

    const drawerClose = event.target.closest('[data-drawer-close]');
    if (drawerClose) {
      closeDrawer(drawerClose.closest('.drawer'));
      return;
    }

    const overlay = event.target.closest('.drawer__overlay');
    if (overlay) {
      closeDrawer(overlay.closest('.drawer'));
      return;
    }

    const scrollButton = event.target.closest('[data-scroll-button]');
    if (scrollButton) {
      const trackId = scrollButton.getAttribute('data-scroll-track');
      const track = trackId ? document.getElementById(trackId) : null;
      if (!track) return;
      const direction = scrollButton.getAttribute('data-direction') === 'prev' ? -1 : 1;
      const amount = Math.max(track.clientWidth * 0.85, 280) * direction;
      track.scrollBy({ left: amount, behavior: 'smooth' });
    }
  }

  function handleGlobalKeydown(event) {
    if (event.key === 'Escape') {
      closeAllDrawers();
    }
  }

  function bindGlobals() {
    if (state.globalsBound) return;
    state.globalsBound = true;
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('keydown', handleGlobalKeydown);
  }

  class AnnouncementRotator {
    constructor(section) {
      this.section = section;
      this.items = toArray(section.querySelectorAll('[data-announcement-item]'));
      this.interval = Number(section.dataset.interval || 5000);
      this.autoRotate = section.dataset.autorotate === 'true';
      this.index = 0;
      this.timer = null;
      if (!this.items.length) return;
      this.show(0);
      if (this.autoRotate && this.items.length > 1 && !window.Shopify?.designMode) {
        this.start();
      }
    }

    show(index) {
      this.index = index;
      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });
    }

    next() {
      this.show((this.index + 1) % this.items.length);
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stop();
    }
  }

  class HeroSlider {
    constructor(section) {
      this.section = section;
      this.slides = toArray(section.querySelectorAll('[data-hero-slide]'));
      this.jumpButtons = toArray(section.querySelectorAll('[data-hero-jump]'));
      this.prevButton = section.querySelector('[data-hero-prev]');
      this.nextButton = section.querySelector('[data-hero-next]');
      this.currentEl = section.querySelector('[data-hero-current]');
      this.totalEl = section.querySelector('[data-hero-total]');
      this.autoRotate = section.dataset.autorotate === 'true';
      this.interval = Number(section.dataset.interval || 6000);
      this.timer = null;
      this.index = 0;
      this.onMouseEnter = () => this.stop();
      this.onMouseLeave = () => this.start();
      this.boundJumpHandlers = [];

      if (!this.slides.length) return;
      if (this.totalEl) this.totalEl.textContent = String(this.slides.length).padStart(2, '0');
      this.bind();
      this.show(0);
      if (this.autoRotate && this.slides.length > 1 && !window.Shopify?.designMode) {
        this.start();
      }
    }

    bind() {
      if (this.prevButton) {
        this.prevHandler = () => {
          this.show((this.index - 1 + this.slides.length) % this.slides.length);
          this.restart();
        };
        this.prevButton.addEventListener('click', this.prevHandler);
      }
      if (this.nextButton) {
        this.nextHandler = () => {
          this.show((this.index + 1) % this.slides.length);
          this.restart();
        };
        this.nextButton.addEventListener('click', this.nextHandler);
      }
      this.jumpButtons.forEach((button) => {
        const handler = () => {
          this.show(Number(button.dataset.heroJump || 0));
          this.restart();
        };
        this.boundJumpHandlers.push({ button, handler });
        button.addEventListener('click', handler);
      });
      this.section.addEventListener('mouseenter', this.onMouseEnter);
      this.section.addEventListener('mouseleave', this.onMouseLeave);
    }

    show(index, scrollIntoView = false) {
      this.index = index;
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });
      this.jumpButtons.forEach((button, buttonIndex) => {
        button.classList.toggle('is-current', buttonIndex === index);
        button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
      });
      if (this.currentEl) this.currentEl.textContent = String(index + 1).padStart(2, '0');
      if (scrollIntoView) this.section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    start() {
      if (!this.autoRotate || this.slides.length <= 1 || window.Shopify?.designMode) return;
      this.stop();
      this.timer = window.setInterval(() => this.show((this.index + 1) % this.slides.length), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    destroy() {
      this.stop();
      if (this.prevButton && this.prevHandler) this.prevButton.removeEventListener('click', this.prevHandler);
      if (this.nextButton && this.nextHandler) this.nextButton.removeEventListener('click', this.nextHandler);
      this.boundJumpHandlers.forEach(({ button, handler }) => button.removeEventListener('click', handler));
      this.section.removeEventListener('mouseenter', this.onMouseEnter);
      this.section.removeEventListener('mouseleave', this.onMouseLeave);
    }
  }

  class ProductSection {
    constructor(section) {
      this.section = section;
      this.form = section.querySelector('[data-product-form]');
      this.variantInput = section.querySelector('[data-variant-id]');
      this.priceEl = section.querySelector('[data-product-price]');
      this.compareEl = section.querySelector('[data-product-compare]');
      this.skuEl = section.querySelector('[data-product-sku]');
      this.statusEl = section.querySelector('[data-product-status]');
      this.submitButton = section.querySelector('[data-add-to-cart]');
      this.addToCartText = section.querySelector('[data-add-to-cart-text]');
      this.optionGroups = toArray(section.querySelectorAll('[data-option-group]'));
      this.thumbs = toArray(section.querySelectorAll('[data-thumbnail]'));
      this.media = toArray(section.querySelectorAll('[data-media-id]'));
      this.moneyFormat = section.dataset.moneyFormat || '${{amount}}';
      this.jsonEl = section.querySelector('[data-product-json]');
      this.variantData = this.jsonEl ? JSON.parse(this.jsonEl.textContent).variants : [];
      this.currentVariant = null;
      this.boundThumbHandlers = [];
      this.boundOptionHandlers = [];

      if (!this.form || !this.variantInput || !this.variantData.length) return;
      this.bind();
      this.setInitialVariant();
    }

    bind() {
      this.optionGroups.forEach((group) => {
        const handler = () => this.updateFromOptions();
        this.boundOptionHandlers.push({ group, handler });
        group.addEventListener('change', handler);
      });
      this.thumbs.forEach((thumb) => {
        const handler = () => {
          this.activateMedia(Number(thumb.dataset.thumbnail || 0));
        };
        this.boundThumbHandlers.push({ thumb, handler });
        thumb.addEventListener('click', handler);
      });
    }

    setInitialVariant() {
      const url = new URL(window.location.href);
      const urlVariantId = Number(url.searchParams.get('variant') || 0);
      const inputVariantId = Number(this.variantInput.value || 0);
      const variant =
        this.variantData.find((item) => Number(item.id) === urlVariantId) ||
        this.variantData.find((item) => Number(item.id) === inputVariantId) ||
        this.variantData[0];
      if (!variant) return;
      this.syncOptionsToVariant(variant);
      this.currentVariant = variant;
      this.updateInterface(variant, false);
    }

    syncOptionsToVariant(variant) {
      this.optionGroups.forEach((group, optionIndex) => {
        const selectedValue = variant.options ? variant.options[optionIndex] : null;
        toArray(group.querySelectorAll('input')).forEach((input) => {
          input.checked = input.value === selectedValue;
        });
      });
    }

    getSelectedOptions() {
      return this.optionGroups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.variantData.find((variant) => {
        return Array.isArray(variant.options) && variant.options.every((value, index) => value === selectedOptions[index]);
      });
    }

    updateFromOptions() {
      const variant = this.findVariant();
      this.updateInterface(variant, true);
    }

    updateInterface(variant, updateUrl = true) {
      if (!variant) {
        if (this.submitButton) this.submitButton.disabled = true;
        if (this.addToCartText) this.addToCartText.textContent = this.submitButton?.dataset.unavailableText || 'Unavailable';
        if (this.statusEl) this.statusEl.textContent = this.submitButton?.dataset.unavailableText || 'Unavailable';
        return;
      }

      this.currentVariant = variant;
      this.variantInput.value = String(variant.id);

      if (this.priceEl) this.priceEl.textContent = formatMoney(variant.price, this.moneyFormat);
      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareEl.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
          this.compareEl.textContent = '';
        }
      }
      if (this.skuEl) this.skuEl.textContent = variant.sku || '';
      if (this.submitButton) this.submitButton.disabled = !variant.available;
      if (this.addToCartText) {
        this.addToCartText.textContent = variant.available
          ? this.submitButton?.dataset.addText || 'Add to cart'
          : this.submitButton?.dataset.soldOutText || 'Sold out';
      }
      if (this.statusEl) {
        this.statusEl.textContent = variant.available ? '' : this.submitButton?.dataset.soldOutText || 'Sold out';
      }
      if (variant.featured_media_id) {
        this.activateMedia(Number(variant.featured_media_id));
      }

      if (updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    activateMedia(mediaId) {
      if (!mediaId) return;
      this.media.forEach((item) => {
        item.classList.toggle('is-active', Number(item.dataset.mediaId) === mediaId);
      });
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', Number(thumb.dataset.thumbnail) === mediaId);
      });
    }

    destroy() {
      this.boundOptionHandlers.forEach(({ group, handler }) => group.removeEventListener('change', handler));
      this.boundThumbHandlers.forEach(({ thumb, handler }) => thumb.removeEventListener('click', handler));
    }
  }

  function initAnnouncement(root = document) {
    root.querySelectorAll('[data-announcement-rotator]').forEach((element) => {
      if (state.announcementInstances.has(element)) return;
      state.announcementInstances.set(element, new AnnouncementRotator(element));
    });
  }

  function initHero(root = document) {
    root.querySelectorAll('[data-hero-slider]').forEach((element) => {
      if (state.heroInstances.has(element)) return;
      state.heroInstances.set(element, new HeroSlider(element));
    });
  }

  function initProducts(root = document) {
    root.querySelectorAll('[data-product-root]').forEach((element) => {
      if (state.productInstances.has(element)) return;
      state.productInstances.set(element, new ProductSection(element));
    });
  }

  function destroyFromMap(map, root) {
    for (const [element, instance] of map.entries()) {
      if (element === root || root.contains(element)) {
        if (typeof instance.destroy === 'function') instance.destroy();
        map.delete(element);
      }
    }
  }

  function init(root = document) {
    bindGlobals();
    initAnnouncement(root);
    initHero(root);
    initProducts(root);
  }

  function destroy(root) {
    destroyFromMap(state.announcementInstances, root);
    destroyFromMap(state.heroInstances, root);
    destroyFromMap(state.productInstances, root);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const heroSection = event.target.closest('[data-hero-slider]');
    if (heroSection && state.heroInstances.has(heroSection)) {
      const slide = event.target.closest('[data-hero-slide]');
      if (slide) {
        state.heroInstances.get(heroSection).show(Number(slide.dataset.slideIndex || 0), true);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
})();
